import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategory } from '../../context/CategoryContext.tsx';
import { AppBar, Toolbar, Typography, Button, IconButton, Drawer, List, ListItem, ListItemText, Box, useTheme, useMediaQuery, Menu, MenuItem, Divider } from '@mui/material';
import { Menu as MenuIcon, Phone, Email, ExpandMore, Close } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { setSelectedCategory } = useCategory();

  const handleProductsClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleProductsClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCategoryClick = useCallback((categoryName: string) => {
    if (categoryName === 'Custom Designs') {
      window.open('https://wa.me/916309561983?text=Hello!%20I%20need%20custom%20furniture%20design.', '_blank');
      handleProductsClose();
      return;
    }
    const categoryList = ['All', 'Sofas', 'Beds', 'Recliners', 'Chairs', 'Wall Padding'];
    const idx = categoryName === 'All Products' ? 0 : categoryList.indexOf(categoryName);
    setSelectedCategory(idx > -1 ? idx : 0);
    handleProductsClose();
    setMobileOpen(false);
    navigate('/');
    setTimeout(() => {
      const productsSection = document.getElementById('products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [setSelectedCategory, navigate, handleProductsClose]);

  const handleNavClick = useCallback((href: string) => {
    setMobileOpen(false);
    if (href === '/contact') {
      navigate('/contact');
    } else if (href === '/') {
      navigate('/');
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(href.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { text: 'Home', href: '/' },
    { text: 'Products', href: '#products', hasDropdown: true },
    { text: 'About', href: '#about' },
    { text: 'Contact', href: '/contact' },
  ];

  const productCategories = [
    { text: 'All Products', href: '#products', icon: '🛋️' },
    { text: 'Sofas', href: '#products', icon: '🛋️' },
    { text: 'Beds', href: '#products', icon: '🛏️' },
    { text: 'Recliners', href: '#products', icon: '💺' },
    { text: 'Chairs', href: '#products', icon: '🪑' },
    { text: 'Wall Padding', href: '#products', icon: '🧱' },
    { text: 'Custom Designs', href: 'https://wa.me/916309561983?text=Hello!%20I%20need%20custom%20furniture%20design.', icon: '✨' },
  ];

  const drawer = (
    <Box sx={{ width: 280, height: '100%', backgroundColor: '#FFF8F0', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid rgba(139,115,85,0.1)' }}>
        <Box component="img" src="/images/products/Logo without BG.png" alt="Logo" sx={{ height: 60, width: 'auto', objectFit: 'contain' }} />
        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: '#8B7355' }}>
          <Close />
        </IconButton>
      </Box>

      <List sx={{ px: 1, flex: 1 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.hasDropdown ? (
              <>
                <ListItem sx={{ borderRadius: 2, mb: 0.5, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(139,115,85,0.08)' } }}>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600, color: '#8B7355', fontSize: '1.1rem' }} />
                </ListItem>
                {productCategories.map((category) => (
                  <ListItem
                    key={category.text}
                    onClick={() => handleCategoryClick(category.text)}
                    sx={{
                      pl: 4,
                      borderRadius: 2,
                      mb: 0.5,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(139,115,85,0.08)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span style={{ fontSize: '1rem' }}>{category.icon}</span>
                      <ListItemText
                        primary={category.text}
                        primaryTypographyProps={{
                          fontWeight: 400,
                          color: '#8B7355',
                          fontSize: '1rem'
                        }}
                      />
                    </Box>
                  </ListItem>
                ))}
                <Divider sx={{ my: 1, backgroundColor: 'rgba(139,115,85,0.1)' }} />
              </>
            ) : (
              <ListItem
                onClick={() => handleNavClick(item.href)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(139,115,85,0.08)' }
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    color: '#8B7355',
                    fontSize: '1.1rem'
                  }}
                />
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(139,115,85,0.1)', display: 'flex', justifyContent: 'center', gap: 2 }}>
        <IconButton href="tel:+916309561983" sx={{ color: '#8B7355', backgroundColor: 'rgba(139,115,85,0.1)' }}>
          <Phone />
        </IconButton>
        <IconButton href="mailto:Ahmed83shareef@gmail.com" sx={{ color: '#8B7355', backgroundColor: 'rgba(139,115,85,0.1)' }}>
          <Email />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: '#FFFFFF', transition: 'all 0.3s ease', color: '#8B7355' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 3, md: 4 }, minHeight: { xs: 64, sm: 70 } }}>
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <Box component="button" onClick={() => navigate('/')} sx={{ border: 'none', background: 'none', padding: 0, display: 'flex', alignItems: 'center', textDecoration: 'none', gap: 1.5, transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)', cursor: 'pointer', '&:hover': { transform: 'scale(1.12) translateY(-3px)', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.4)) brightness(1.15)' }, position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, sm: 1.5 } }}>
                <Box component="img" src="/images/products/Logo without BG.png" alt="Logo" sx={{ height: { xs: 60, sm: 85, md: 100 }, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2)) drop-shadow(0 4px 12px rgba(0,0,0,0.3)) drop-shadow(0 8px 16px rgba(0,0,0,0.15))', transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 0.2, alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontFamily: '"Cinzel", serif', fontWeight: 700, color: '#8B7355', fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' }, letterSpacing: '1px', margin: 0, lineHeight: 1.2, textTransform: 'uppercase' }}>
                    AHMED SHAREEF
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: '"Playfair Display", serif', color: 'rgba(139,115,85,0.7)', fontSize: { xs: '0.7rem', sm: '0.78rem', md: '0.85rem' }, fontWeight: 400, letterSpacing: '1px', textTransform: 'none', fontStyle: 'italic' }}>
                    Sofa makers
                  </Typography>
                </Box>
              </Box>
            </Box>
          </motion.div>

          {!isMobile ? (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              {menuItems.map((item, index) => (
                <motion.div key={item.text} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                  {item.hasDropdown ? (
                    <>
                      <Button onClick={handleProductsClick} endIcon={<ExpandMore />} sx={{ color: '#8B7355', fontWeight: 500, textTransform: 'none', fontSize: '1.1rem', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(139,115,85,0.08)' } }}>
                        {item.text}
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleProductsClose}
                        sx={{
                          '& .MuiPaper-root': {
                            mt: 1,
                            minWidth: 250,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(139,115,85,0.1)'
                          }
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      >
                        {productCategories.map((category, index) => (
                          <MenuItem
                            key={category.text}
                            onClick={() => handleCategoryClick(category.text)}
                            sx={{
                              color: '#8B7355',
                              fontWeight: 500,
                              py: 1.5,
                              px: 2.5,
                              borderBottom: index === 0 ? '1px solid rgba(139,115,85, 0.1)' : 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(139,115,85, 0.08)',
                                transform: 'translateX(4px)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                              <span style={{ fontSize: '1.2rem' }}>{category.icon}</span>
                              <span style={{ flex: 1 }}>{category.text}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : (
                    <Button onClick={() => handleNavClick(item.href)} sx={{ color: '#8B7355', fontWeight: 500, textTransform: 'none', fontSize: '1.1rem', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(139,115,85,0.08)' } }}>
                      {item.text}
                    </Button>
                  )}
                </motion.div>
              ))}
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <IconButton href="tel:+916309561983" sx={{ color: '#8B7355' }}><Phone /></IconButton>
                <IconButton href="mailto:Ahmed83shareef@gmail.com" sx={{ color: '#8B7355' }}><Email /></IconButton>
              </Box>
            </Box>
          ) : (
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={() => setMobileOpen(true)} sx={{ color: '#8B7355' }}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <AnimatePresence>
        {mobileOpen && (
          <Drawer
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: 280,
                boxShadow: '-4px 0 20px rgba(0,0,0,0.1)'
              }
            }}
          >
            {drawer}
          </Drawer>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
